import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/lib/auth-context';

// Layouts
import PublicLayout from '@/components/layout/PublicLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';

// Public pages
import HomePage from '@/pages/public/HomePage';
import ServicesPage from '@/pages/public/ServicesPage';
import ContactPage from '@/pages/public/ContactPage';
import BookAppointmentPage from '@/pages/public/BookAppointmentPage';

// Auth pages
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';

// Dashboard pages
import DashboardOverview from '@/pages/dashboard/DashboardOverview';
import ClientsPage from '@/pages/dashboard/ClientsPage';
import BoardsPage from '@/pages/dashboard/BoardsPage';
import BoardDetailPage from '@/pages/dashboard/BoardDetailPage';
import SubtaskDetailPage from '@/pages/dashboard/SubtaskDetailPage';
import ChatPage from '@/pages/dashboard/ChatPage';
import BillingPage from '@/pages/dashboard/BillingPage';
import TeamPage from '@/pages/dashboard/TeamPage';
import LeadsPage from '@/pages/dashboard/LeadsPage';
import SettingsPage from '@/pages/dashboard/SettingsPage';
import ComplianceCalendarPage from '@/pages/dashboard/ComplianceCalendarPage';
import MyDocumentsPage from '@/pages/dashboard/MyDocumentsPage';
import ActivityLogPage from '@/pages/dashboard/ActivityLogPage';
import OpenTasksPage from '@/pages/dashboard/OpenTasksPage';
import PendingInvoicesPage from '@/pages/dashboard/PendingInvoicesPage';
import PendingFeesPage from '@/pages/dashboard/PendingFeesPage';
import MyTasksPage from '@/pages/dashboard/MyTasksPage';
import UpcomingDuePage from '@/pages/dashboard/UpcomingDuePage';
import DirectorsPage from '@/pages/dashboard/DirectorsPage';
import DirectorDetailPage from '@/pages/dashboard/DirectorDetailPage';
import NotificationsPage from '@/pages/dashboard/NotificationsPage';

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Role constants for ProtectedRoute (prevents re-render from inline arrays)
const ROLES_ADMIN_STAFF = ['admin', 'staff'];
const ROLES_ADMIN_STAFF_CLIENT = ['admin', 'staff', 'client'];
const ROLES_ADMIN_CLIENT = ['admin', 'client'];
const ROLES_ADMIN = ['admin'];

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/book-appointment" element={<BookAppointmentPage />} />
      </Route>

      {/* Auth routes */}
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />

      {/* Dashboard routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardOverview />} />
        <Route path="clients" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_STAFF}>
            <ClientsPage />
          </ProtectedRoute>
        } />
        <Route path="directors" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_STAFF}>
            <DirectorsPage />
          </ProtectedRoute>
        } />
        <Route path="directors/:directorId" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_STAFF}>
            <DirectorDetailPage />
          </ProtectedRoute>
        } />
        <Route path="boards" element={<BoardsPage />} />
        <Route path="boards/:boardId" element={<BoardDetailPage />} />
        <Route path="boards/:boardId/task/:taskId/subtask/:subtaskId" element={<SubtaskDetailPage />} />
        <Route path="my-tasks" element={<MyTasksPage />} />
        <Route path="upcoming-due" element={<UpcomingDuePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="billing" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_CLIENT}>
            <BillingPage />
          </ProtectedRoute>
        } />
        <Route path="team" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN}>
            <TeamPage />
          </ProtectedRoute>
        } />
        <Route path="leads" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN}>
            <LeadsPage />
          </ProtectedRoute>
        } />
        <Route path="documents" element={<MyDocumentsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="calendar" element={<ComplianceCalendarPage />} />
        <Route path="activity-log" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN}>
            <ActivityLogPage />
          </ProtectedRoute>
        } />
        <Route path="open-tasks" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_STAFF}>
            <OpenTasksPage />
          </ProtectedRoute>
        } />
        <Route path="pending-invoices" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_STAFF}>
            <PendingInvoicesPage />
          </ProtectedRoute>
        } />
        <Route path="pending-fees" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_STAFF}>
            <PendingFeesPage />
          </ProtectedRoute>
        } />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
